import { Theme } from '@mui/material/styles'

/**
 * Centralized container/layout styles
 * Use these instead of repeating container styles across components
 */

/**
 * Box container - centered flex column with padding
 */
export const boxContainerStyles = (theme: Theme) => ({
  position: 'relative' as const,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(2),
})

/**
 * Alert box with red border - flex row layout
 */
export const alertBoxStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  width: '100%',
  border: '1px solid',
  borderColor: 'rgba(201, 50, 46, 0.5)',
  padding: theme.spacing(1),
})

/**
 * Account/warning box - yellow border variant
 */
export const accountBoxStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  width: '100%',
  border: '1px solid',
  borderColor: 'rgb(251, 193, 30)',
  padding: theme.spacing(1),
})

/**
 * Grid container - centered flex with max height
 */
export const gridContainerStyles = () => ({
  display: 'flex',
  maxHeight: '100vh',
  justifyContent: 'center',
})

/**
 * Grid toolbar container - space between layout
 */
export const gridToolbarContainerStyles = () => ({
  display: 'flex',
  flexDirection: 'row' as const,
  justifyContent: 'space-between',
})

/**
 * Button container - centered with gap
 */
export const buttonContainerStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  marginTop: theme.spacing(2),
  gap: theme.spacing(1),
})

/**
 * Popover container styles
 */
export const popoverStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 39, 53, 0.85)' : 'rgba(255, 255, 255, 0.85)',
  zIndex: 1000,
})

/**
 * Dialog content wrapper (simple centering)
 */
export const dialogContentWrapperStyles = () => ({
  width: 'auto',
  maxWidth: '700px',
  margin: '0 auto',
})

/**
 * Base grid styles for dialogs/panels
 */
export const gridStyles = (theme: Theme) => ({
  width: '100%',
  color: '#fff',
  minHeight: '50vh',
  maxHeight: '95vh',
  // padding: '20px',
  '& .MuiDialogContent-root': {
    width: '100%',
    padding: '20px',
  },
  '& .MuiTextField-root': {
    margin: '10px 0',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: theme.palette.mode === 'dark' ? '' : '1px solid',
        borderColor: theme.palette.mode === 'dark' ? '' : 'rgba(0,0,0,0.1)',
      },
      '&:hover fieldset': {
        border: theme.palette.mode === 'dark' ? '' : '1px solid',
        borderColor: theme.palette.mode === 'dark' ? '' : 'rgba(0,0,0,1)',
      },
      '&.Mui-focused fieldset': {
        border: '2px solid rgb(159, 176, 219)',
      },
    },
  },
  '& .MuiInputBase-root': {
    height: '45px',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgb(22, 28, 42)' : 'rgba(255,255,255,1)',
  },
  '& input[type="number"]': {
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&[type=number]': {
      MozAppearance: 'textfield',
    },
  },
})

/**
 * Filter panel props for DataGrid
 */
export const filterPanelProps = () => ({
  '& .MuiDataGrid-filterForm': {
    width: '100%',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    '& .MuiFormControl-root': {
      flex: '1 1 auto',
      margin: '0',
      minWidth: '100px',
      '& .MuiInputBase-root': {
        padding: '4px',
        borderRadius: '4px',
      },
    },
  },
  '& .MuiDataGrid-filterFormValueInput': {
    flex: '2 1 auto',
    minWidth: '150px',
    '& input': {
      padding: '6px 8px',
      width: '100%',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
})

/**
 * Dual row layout - space between with relative positioning
 */
export const dualRowStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  position: 'relative' as const,
})

/**
 * Phone row layout - right-aligned
 */
export const phoneRowStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
  position: 'relative' as const,
})
